import axios from 'axios';

// const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://timesheetapi.primesourcellp.com/api'; 
// const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';
// const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://timesheetdemoapi.primesourcellp.com/api';
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://1api.primesourcellp.com/api';

// Create axios instance
const api = axios.create({
  // eslint-disable-next-line no-undef
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      // Dispatch custom event for navigation without full page reload
      if (window.location.pathname !== '/login') {
        const event = new CustomEvent('unauthorized', { detail: { path: '/login' } });
        window.dispatchEvent(event);
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/auth/signin', credentials),
  register: (userData) => api.post('/auth/signup', userData),
  getProfile: () => api.get('/employee/profile'),
  getManagerProfile: () => api.get('/manager/profile'),
  initialize: () => api.post('/auth/init'),
  canRegisterSuperAdmin: () => api.get('/auth/can-register-superadmin'),
};

// User API
export const userAPI = {
  // Super Admin
  createAdmin: (userData) => api.post('/super-admin/admins', userData),
  getAllAdmins: () => api.get('/super-admin/admins'),
  getAllSuperAdmins: () => api.get('/super-admin/users?role=SUPER_ADMIN'),
  createManager: (userData) => api.post('/admin/managers', userData),
  getAllManagers: () => api.get('/super-admin/managers'), // For Super Admin
  getAllManagersAdmin: () => api.get('/admin/managers'), // For Admin
  getAllEmployees: () => api.get('/super-admin/employees'),
  getAllUsers: (params) => api.get('/super-admin/users', { params }),
  updateUser: (userId, userData) => api.put(`/super-admin/users/${userId}`, userData),
  updateUserPassword: (userId, passwordData) => api.put(`/super-admin/users/${userId}/password`, passwordData),
  updateUserPasswordAdmin: (userId, passwordData) => api.put(`/admin/users/${userId}/password`, passwordData),
  deleteUser: (userId) => api.delete(`/super-admin/users/${userId}`),
  reactivateUser: (userId) => api.put(`/super-admin/users/${userId}/reactivate`),
  permanentlyDeleteUser: (userId) => api.delete(`/super-admin/users/${userId}/permanent`),
  getUserById: (userId) => api.get(`/super-admin/users/${userId}`),
  getUserDetails: (userId) => api.get(`/super-admin/users/${userId}/details`),
  getDashboardStats: () => api.get('/super-admin/dashboard/stats'),
  getRecentAdmins: () => api.get('/super-admin/dashboard/recent-admins'),
  globalSearch: (query) => api.get(`/super-admin/search?q=${encodeURIComponent(query)}`),
  
  // Admin
  createEmployee: (userData) => api.post('/admin/employees', userData),
  getEmployees: () => api.get('/admin/employees'),
  getManagersAndEmployeesAdmin: () => api.get('/admin/managers-and-employees'),
  getEmployeeDetails: (employeeId) => api.get(`/admin/employees/${employeeId}/details`),
  updateEmployee: (employeeId, userData) => api.put(`/admin/employees/${employeeId}`, userData),
  updateUserAdmin: (userId, userData) => api.put(`/admin/employees/${userId}`, userData), // Admin can update both managers and employees
  deleteEmployee: (employeeId) => api.delete(`/admin/employees/${employeeId}`),
  reactivateEmployee: (employeeId) => api.put(`/admin/employees/${employeeId}/reactivate`),
  reactivateManager: (managerId) => api.put(`/admin/managers/${managerId}/reactivate`),
  permanentlyDeleteEmployee: (employeeId) => api.delete(`/admin/employees/${employeeId}/permanent`),
  getAdminDashboardStats: () => api.get('/admin/dashboard/stats'),
  getAdminRecentTimesheets: () => api.get('/admin/dashboard/recent-timesheets'),
  globalSearch: (query) => api.get(`/admin/search?q=${encodeURIComponent(query)}`),
};

// Timesheet API
export const timesheetAPI = {
  // Employee
  createTimesheet: (timesheetData) => api.post('/employee/timesheets', timesheetData),
  updateTimesheet: (timesheetId, timesheetData) => api.put(`/employee/timesheets/${timesheetId}`, timesheetData),
  submitTimesheet: (timesheetId) => api.post(`/employee/timesheets/${timesheetId}/submit`),
  deleteTimesheet: (timesheetId) => api.delete(`/employee/timesheets/${timesheetId}`),
  getMyTimesheets: (params) => api.get('/employee/timesheets', { params }),
  getTimesheetById: (timesheetId) => api.get(`/employee/timesheets/${timesheetId}`),
  
  // Admin
  getPendingTimesheets: (params) => api.get('/admin/timesheets/pending', { params }),
  approveTimesheet: (timesheetId) => api.post(`/admin/timesheets/${timesheetId}/approve`),
  rejectTimesheet: (timesheetId, rejectionReason) => api.post(`/admin/timesheets/${timesheetId}/reject`, { rejectionReason }),
  getAllTimesheets: (params) => api.get('/admin/timesheets', { params }),
  getAdminTimesheetById: (timesheetId) => api.get(`/admin/timesheets/${timesheetId}`),
  updateTimesheetStatus: (timesheetId, status) => api.put(`/admin/timesheets/${timesheetId}/status`, { status }),
  getTimesheetsByDateRange: (startDate, endDate) => api.get(`/admin/timesheets/date-range?startDate=${startDate}&endDate=${endDate}`),
  getUserTimesheets: (userId) => api.get(`/admin/users/${userId}/timesheets`),
  
  // Super Admin
  getSuperAdminTimesheets: (params) => api.get('/super-admin/timesheets', { params }),
  getSuperAdminTimesheetById: (timesheetId) => api.get(`/super-admin/timesheets/${timesheetId}`),
  updateSuperAdminTimesheetStatus: (timesheetId, status) => api.put(`/super-admin/timesheets/${timesheetId}/status`, { status }),
  getSuperAdminUserTimesheets: (userId) => api.get(`/super-admin/users/${userId}/timesheets`),
  getRecentTimesheets: () => api.get('/super-admin/dashboard/recent-timesheets'),
  
  // Manager
  getManagerUserTimesheets: (userId) => api.get(`/manager/users/${userId}/timesheets`),
};

// Profile API
export const profileAPI = {
  // Employee endpoints
  getProfile: () => api.get('/employee/profile'),
  updateProfile: (profileData) => api.put('/employee/profile', profileData),
  
  // Manager endpoints
  getManagerProfile: () => api.get('/manager/profile'),
  updateManagerProfile: (profileData) => api.put('/manager/profile', profileData),
  
  // Admin endpoints
  getAdminProfile: () => api.get('/admin/profile'),
  updateAdminProfile: (profileData) => api.put('/admin/profile', profileData),
  
  // Super Admin endpoints
  getSuperAdminProfile: () => api.get('/super-admin/profile'),
  updateSuperAdminProfile: (profileData) => api.put('/super-admin/profile', profileData),
};

// Project API
export const projectAPI = {
  // Admin/Super Admin endpoints
  createProject: (projectData) => api.post('/admin/projects', projectData),
  getAllProjects: (params) => api.get('/admin/projects', { params }),
  getProjectById: (projectId) => api.get(`/admin/projects/${projectId}`),
  getAssignedProjects: () => api.get('/admin/projects/assigned'),
  assignAdminToProject: (projectId, adminId) => api.post(`/admin/projects/${projectId}/assign-admin/${adminId}`),
  removeAdminFromProject: (projectId, adminId) => api.delete(`/admin/projects/${projectId}/assign-admin/${adminId}`),
  assignSuperAdminToProject: (projectId, superAdminId) => api.post(`/admin/projects/${projectId}/assign-super-admin/${superAdminId}`),
  removeSuperAdminFromProject: (projectId, superAdminId) => api.delete(`/admin/projects/${projectId}/assign-super-admin/${superAdminId}`),
  updateProject: (projectId, projectData) => api.put(`/admin/projects/${projectId}`, projectData),
  deleteProject: (projectId) => api.delete(`/admin/projects/${projectId}`),
  reactivateProject: (projectId) => api.put(`/admin/projects/${projectId}/reactivate`),
  assignEmployeeToProject: (projectId, employeeId) => api.post(`/admin/projects/${projectId}/assign/${employeeId}`),
  removeEmployeeFromProject: (projectId, employeeId) => api.delete(`/admin/projects/${projectId}/assign/${employeeId}`),
  assignManagerToProject: (projectId, managerId) => api.post(`/admin/projects/${projectId}/assign-manager/${managerId}`),
  removeManagerFromProject: (projectId, managerId) => api.delete(`/admin/projects/${projectId}/assign-manager/${managerId}`),
  
  // Employee endpoints
  getMyAssignedProjects: () => api.get('/employee/projects'),
  
  // Manager endpoints
  getManagerProjects: (params) => api.get('/manager/projects', { params }),
  createManagerProject: (projectData) => api.post('/manager/projects', projectData),
  getManagerEmployees: () => api.get('/manager/employees'),
  getAllEmployeesForManager: () => api.get('/manager/employees/all'),
  createManagerEmployee: (employeeData) => api.post('/manager/employees', employeeData),
  getManagerEmployeeDetails: (employeeId) => api.get(`/manager/employees/${employeeId}`),
  updateManagerEmployee: (employeeId, employeeData) => api.put(`/manager/employees/${employeeId}`, employeeData),
  deleteManagerEmployee: (employeeId) => api.delete(`/manager/employees/${employeeId}`),
  reactivateManagerEmployee: (employeeId) => api.put(`/manager/employees/${employeeId}/reactivate`),
  assignManagerEmployeeToProject: (projectId, employeeId) => api.post(`/manager/projects/${projectId}/assign/${employeeId}`),
  removeManagerEmployeeFromProject: (projectId, employeeId) => api.delete(`/manager/projects/${projectId}/remove/${employeeId}`),
  getManagerProjectEmployees: (projectId) => api.get(`/manager/projects/${projectId}/employees`),
  
  // Manager admin-like operations
  updateAnyEmployee: (employeeId, employeeData) => api.put(`/manager/employees/${employeeId}/update`, employeeData),
  deactivateAnyEmployee: (employeeId) => api.delete(`/manager/employees/${employeeId}/deactivate`),
  permanentlyDeleteManagerEmployee: (employeeId) => api.delete(`/manager/employees/${employeeId}/permanent`),
  updateAnyProject: (projectId, projectData) => api.put(`/manager/projects/${projectId}/update`, projectData),
  deactivateAnyProject: (projectId) => api.delete(`/manager/projects/${projectId}/deactivate`),
  reactivateManagerProject: (projectId) => api.put(`/manager/projects/${projectId}/reactivate`),
  updateManagerProject: (projectId, projectData) => api.put(`/manager/projects/${projectId}/update`, projectData),
  deleteManagerProject: (projectId) => api.delete(`/manager/projects/${projectId}/deactivate`),
  
  // Manager timesheet management
  getManagerPendingTimesheets: (params) => api.get('/manager/timesheets/pending', { params }),
  approveManagerTimesheet: (timesheetId) => api.post(`/manager/timesheets/${timesheetId}/approve`),
  rejectManagerTimesheet: (timesheetId, rejectionReason) => api.post(`/manager/timesheets/${timesheetId}/reject`, { rejectionReason }),
  getAllManagerTimesheets: (params) => api.get('/manager/timesheets', { params }),
  getManagerTimesheetById: (timesheetId) => api.get(`/manager/timesheets/${timesheetId}`),
  getManagerTimesheetsByDateRange: (startDate, endDate) => api.get(`/manager/timesheets/date-range?startDate=${startDate}&endDate=${endDate}`),
  getManagerDashboardStats: () => api.get('/manager/dashboard/stats'),
  getManagerRecentTimesheets: () => api.get('/manager/dashboard/recent-timesheets'),
  getManagerGlobalSearch: (query) => api.get(`/manager/search?q=${encodeURIComponent(query)}`),
  
  // Notification methods
  getNotifications: () => api.get('/notifications'),
  markNotificationAsRead: (notificationId) => api.put(`/notifications/${notificationId}/read`),
  markAllNotificationsAsRead: () => api.put('/notifications/read-all'),
  createNotification: (notificationData) => api.post('/notifications', notificationData),
};

// Timesheet Revocation API
export const revocationAPI = {
  revokeTimesheet: (timesheetId, revocationReason) => 
    api.put(`/admin/timesheets/${timesheetId}/revoke`, { revocationReason }),
  revokeTimesheetSuperAdmin: (timesheetId, revocationReason) => 
    api.put(`/super-admin/timesheets/${timesheetId}/revoke`, { revocationReason }),
};

// Notification API
export const notificationAPI = {
  getNotifications: () => api.get('/notifications'),
  markNotificationAsRead: (notificationId) => api.put(`/notifications/${notificationId}/read`),
  markAllNotificationsAsRead: () => api.put('/notifications/read-all'),
  clearAllNotifications: () => api.delete('/notifications/clear-all'),
  createNotification: (notificationData) => api.post('/notifications', notificationData),
};

// Excel Export API
export const excelAPI = {
  exportTimesheets: (startDate, endDate, employeeIds = []) => {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (employeeIds && employeeIds.length > 0) {
      employeeIds.forEach(id => params.append('employeeIds', id));
    }
    return api.get(`/admin/export/timesheets?${params.toString()}`, {
      responseType: 'blob'
    });
  },
  exportEmployees: () => api.get('/admin/export/employees', {
    responseType: 'blob'
  }),
  exportProjects: () => api.get('/admin/export/projects', {
    responseType: 'blob'
  }),
  // Manager Excel Export API
  exportTimesheetsManager: (startDate, endDate) => {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    return api.get(`/manager/export/timesheets?${params.toString()}`, {
      responseType: 'blob'
    });
  },
  exportEmployeesManager: () => api.get('/manager/export/employees', {
    responseType: 'blob'
  }),
  exportProjectsManager: () => api.get('/manager/export/projects', {
    responseType: 'blob'
  }),
  // Super Admin Excel Export API
  exportTimesheetsSuperAdmin: (startDate, endDate, employeeIds = []) => {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (employeeIds && employeeIds.length > 0) {
      employeeIds.forEach(id => params.append('employeeIds', id));
    }
    return api.get(`/super-admin/export/timesheets?${params.toString()}`, {
      responseType: 'blob'
    });
  },
  exportEmployeesSuperAdmin: () => api.get('/super-admin/export/employees', {
    responseType: 'blob'
  }),
  exportProjectsSuperAdmin: () => api.get('/super-admin/export/projects', {
    responseType: 'blob'
  }),
};

export default api;

